import { useState, useRef } from 'react';
import {
    Zap, Copy, ExternalLink, MessageSquare, AlertCircle, Sparkles, ChevronDown, ChevronUp,
} from 'lucide-react';
import { classifyProduct, ClassificationResult } from '../services/api';
import ConfidenceGauge from '../components/ConfidenceGauge';
import FeedbackModal from '../components/FeedbackModal';
import toast from 'react-hot-toast';

const SAMPLE_PRODUCTS = [
    'Wireless Bluetooth headphones with active noise cancellation, over-ear',
    'Women\'s leather handbag with gold-tone hardware and removable strap',
    'Stainless steel kitchen knife set 8-piece with wooden block',
    'Children\'s remote-controlled toy car electric battery 1:18 scale',
    '4K Ultra HD Smart TV 65 inch OLED flat screen with HDR',
];

// 阶段消息：根据已等待时长给出语义化提示，而非裸露秒数
function getPhaseMessage(elapsed: number): string {
    if (elapsed < 2)  return 'AI 正在分析产品描述…';
    if (elapsed < 5)  return 'AI 正在匹配 HTS 编码…';
    if (elapsed < 10) return 'AI 正在生成分类依据…';
    return 'AI 仍在处理，复杂品类耗时稍长…';
}

export default function ClassifyPage() {
    const [description,     setDescription]     = useState('');
    const [result,          setResult]          = useState<ClassificationResult | null>(null);
    const [loading,         setLoading]         = useState(false);
    const [elapsed,         setElapsed]         = useState(0);       // 骨架屏阶段消息使用
    const [completionTime,  setCompletionTime]  = useState<number | null>(null); // 完成后展示
    const [showAlts,        setShowAlts]        = useState(false);
    const [showFeedback,    setShowFeedback]    = useState(false);
    const startTimeRef = useRef<number>(0);

    const handleClassify = async () => {
        if (!description.trim() || description.trim().length < 5) {
            toast.error('请输入产品描述（至少 5 个字符）');
            return;
        }
        setLoading(true);
        setElapsed(0);
        setResult(null);
        setCompletionTime(null);
        startTimeRef.current = Date.now();

        // 仅用于驱动骨架屏阶段消息切换，不在按钮上展示
        const timer = setInterval(() => setElapsed((s) => s + 0.1), 100);

        try {
            const data = await classifyProduct(description.trim());
            const usedMs = Date.now() - startTimeRef.current;
            setResult(data);
            setCompletionTime(usedMs / 1000);
            toast.success('分类完成！');
        } catch (err: unknown) {
            const axiosMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            const message = axiosMsg || (err instanceof Error ? err.message : '分类失败，请稍后重试');
            toast.error(message);
        } finally {
            clearInterval(timer);
            setLoading(false);
        }
    };

    const copyCode = (code: string) => {
        navigator.clipboard.writeText(code);
        toast.success('HTS 编码已复制！');
    };

    const confidenceColor = (c: number) =>
        c >= 80 ? 'badge-green' : c >= 50 ? 'badge-yellow' : 'badge-red';

    return (
        <div className="p-4 md:p-6 max-w-4xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                    <Zap size={20} className="text-brand-400" />
                    <h1 className="text-xl font-bold text-white">AI 产品分类</h1>
                    <span className="badge badge-blue ml-1">Qwen / DeepSeek</span>
                </div>
                <p className="text-slate-400 text-sm">输入产品描述，AI 自动匹配美国 HTS 10位编码与关税税率</p>
            </div>

            {/* Input area */}
            <div className="card mb-4">
                <label className="section-header block">产品描述</label>
                <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && e.ctrlKey && handleClassify()}
                    placeholder="请用英文描述产品材质、功能、规格、用途等...&#10;例如: Portable wireless Bluetooth speaker with waterproof IPX5 rating, 20W output"
                    rows={4}
                    className="input-field resize-none text-sm leading-relaxed"
                />
                <div className="flex items-center justify-between mt-3">
                    <div className="flex flex-wrap gap-2">
                        {SAMPLE_PRODUCTS.map((p, i) => (
                            <button
                                key={i}
                                onClick={() => setDescription(p)}
                                className="text-xs text-slate-500 hover:text-brand-400 bg-surface-2 hover:bg-brand-900/30 px-2 py-1 rounded-lg border border-white/[0.06] hover:border-brand-500/30 transition-all duration-150 truncate max-w-[180px]"
                            >
                                {p.slice(0, 25)}…
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={handleClassify}
                        disabled={loading}
                        className="btn-primary flex-shrink-0 ml-4"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                分类中…
                            </>
                        ) : (
                            <>
                                <Sparkles size={16} />
                                开始分类
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Loading skeleton — 阶段消息驱动，不暴露裸露秒数 */}
            {loading && (
                <div className="card space-y-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-4 h-4 border-2 border-brand-500/40 border-t-brand-400 rounded-full animate-spin flex-shrink-0" />
                        <span className="text-sm text-slate-400">{getPhaseMessage(elapsed)}</span>
                    </div>
                    <div className="animate-pulse space-y-3">
                        <div className="h-4 bg-white/[0.06] rounded-lg w-1/3" />
                        <div className="h-12 bg-white/[0.06] rounded-xl w-2/3" />
                        <div className="h-4 bg-white/[0.06] rounded-lg w-full" />
                        <div className="h-4 bg-white/[0.06] rounded-lg w-4/5" />
                    </div>
                </div>
            )}

            {/* Result */}
            {result && !loading && (
                <div className="space-y-4 animate-slide-up">
                    {/* Main result card */}
                    <div className="card glow">
                        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                            {/* Gauge */}
                            <div className="flex-shrink-0">
                                <ConfidenceGauge value={result.confidence} size="lg" />
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <p className="section-header">HTS 编码（10位）</p>
                                    <span className={confidenceColor(result.confidence)}>
                                        置信度 {result.confidence}%
                                    </span>
                                    {completionTime !== null && (
                                        <span className="text-[10px] text-slate-600 ml-auto">
                                            用时 {completionTime.toFixed(1)} 秒
                                        </span>
                                    )}
                                </div>

                                <div className="flex items-center gap-3 mb-3">
                                    <span className="font-mono text-3xl font-bold text-gradient">
                                        {result.hts_code_formatted}
                                    </span>
                                    <button
                                        onClick={() => copyCode(result.hts_code_formatted)}
                                        className="text-slate-500 hover:text-brand-400 transition-colors p-1"
                                        title="复制 HTS 编码"
                                    >
                                        <Copy size={16} />
                                    </button>
                                    <a
                                        href={`https://hts.usitc.gov/?query=${result.hts_code}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-slate-500 hover:text-brand-400 transition-colors p-1"
                                        title="在 USITC 官网查看"
                                    >
                                        <ExternalLink size={16} />
                                    </a>
                                </div>

                                <p className="text-slate-300 text-sm mb-3 leading-relaxed">{result.hts_description}</p>

                                <div className="flex flex-wrap gap-3">
                                    <div className="bg-surface-2 rounded-xl px-3 py-2 text-center">
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">基础税率</p>
                                        <p className="text-lg font-bold text-amber-400">
                                            {(result.duty_rate * 100).toFixed(1)}%
                                        </p>
                                    </div>
                                    <div className="bg-surface-2 rounded-xl px-3 py-2 text-center">
                                        <p className="text-[10px] text-slate-500 uppercase tracking-wide">章节</p>
                                        <p className="text-lg font-bold text-white">{result.hts_code.slice(0, 2)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Rationale */}
                    <div className="card">
                        <p className="section-header">AI 分类依据</p>
                        <p className="text-slate-300 text-sm leading-relaxed">{result.rationale}</p>
                    </div>

                    {/* Alternatives */}
                    <div className="card">
                        <button
                            onClick={() => setShowAlts(!showAlts)}
                            className="flex items-center justify-between w-full"
                        >
                            <p className="section-header mb-0">备选分类（{result.alternatives.length}）</p>
                            {showAlts ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                        </button>

                        {showAlts && (
                            <div className="mt-3 space-y-2 animate-fade-in">
                                {result.alternatives.map((alt, i) => (
                                    <div key={i} className="flex items-center gap-3 bg-surface-2 rounded-xl p-3">
                                        <span className="hts-code flex-shrink-0">
                                            {alt.hts_code.replace(/(\d{4})(\d{2})(\d{4})/, '$1.$2.$3')}
                                        </span>
                                        <p className="text-slate-400 text-sm flex-1 min-w-0 line-clamp-1">{alt.description}</p>
                                        <span className={`flex-shrink-0 ${confidenceColor(alt.confidence)}`}>
                                            {alt.confidence}%
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowFeedback(true)}
                            className="btn-secondary flex items-center gap-2"
                        >
                            <MessageSquare size={15} />
                            分类纠错
                        </button>
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                            <AlertCircle size={12} />
                            <span>结果仅供参考，正式申报请咨询持牌报关行</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Feedback modal */}
            {showFeedback && result && (
                <FeedbackModal
                    classification={result}
                    onClose={() => setShowFeedback(false)}
                />
            )}
        </div>
    );
}
